import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../ampli';
import Globe from '../../../assets/images/pluvaAutoLanding/point_emoiji_globe-showing.png';
import Satellite from '../../../assets/images/pluvaAutoLanding/point_emoji_satellite.png';
import Tractor from '../../../assets/images/pluvaAutoLanding/point_emoji_tractor.png';
import PVA_IMAGE from '../../../assets/images/pva_image_250917.jpg';
import { Icon } from '../../../components/common/icon/Icon';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { postMessageToRN } from '../../../utils/RNCommunicator/postMessageToRN.util';

interface IKeyFeature {
  icon: string;
  content: string;
  helperText: string;
  color: string;
}

const keyFeatures: IKeyFeature[] = [
  {
    icon: Tractor,
    content: '1,000대 이상 판매 검증된 제품',
    helperText: '이미 전국 곳곳에 많은 사례 ',
    color: '#E1FAFF',
  },
  {
    icon: Globe,
    content: '전국 대리점, 본사 매니저 구축',
    helperText: '설치/교육/AS 모두 가능한 제품',
    color: '#F2F2F7',
  },
  {
    icon: Satellite,
    content: '끊김없는 GPS 자율주행 지원',
    helperText: '트랙터/이앙기 적용 가능한 제품',
    color: '#D6F4E5',
  },
];

const KeyFeatureCard = ({ feature }: { feature: IKeyFeature }) => {
  return (
    <Card>
      <PointIconWrapper bgColor={feature.color}>
        <PointIcon src={feature.icon} alt={`${feature.content}의 아이콘`} />
      </PointIconWrapper>
      <CardContentWrapper>
        <CardContent>{feature.content}</CardContent>
        <CardHelperText>{feature.helperText}</CardHelperText>
      </CardContentWrapper>
    </Card>
  );
};

export const TopSection = () => {
  const navigate = useNavigate();

  const handlePluvaAutoInquiryButton = () => {
    ampli.오토최저가문의버튼클릭();
    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: 'tel:15335502' });
    } else {
      window.location.href = 'tel:15335502';
    }
  };

  return (
    <Container>
      <TitleSection>
        <TitleHelperText>{'원하는대로 작업되고,\n내 농지를 화면에서 보여주는'}</TitleHelperText>
        <Title>
          {'프리미엄 '}
          <TitleEasterEggs onClick={() => navigate(pageInfo.pluvaAutoBusinessPage.path)}>
            {'플루바오토'}
          </TitleEasterEggs>
        </Title>
      </TitleSection>

      <PvaImage src={PVA_IMAGE} alt={'PVA_IMAGE'} />

      <KeyFeatureGroup>
        {keyFeatures.map((feature: IKeyFeature) => {
          return <KeyFeatureCard feature={feature} key={feature.content} />;
        })}
      </KeyFeatureGroup>
      <Button onClick={handlePluvaAutoInquiryButton}>
        <ButtonTextWrapper>
          <Icon name={'Phone'} width={20} height={20} fill={'#ffffff'} />
          <ButtonHighlightText>{'1533-5502'}</ButtonHighlightText>
          <ButtonText>{'간편 문의 전화'}</ButtonText>
        </ButtonTextWrapper>
      </Button>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 48px 24px;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  align-self: stretch;
`;

const PvaImage = styled.img`
  height: 100%;
  align-self: stretch;
  aspect-ratio: 6/5;
  border-radius: 8px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const Title = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XXX-Large/xxxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */
`;

const TitleEasterEggs = styled.span``;

const TitleHelperText = styled.pre`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const KeyFeatureGroup = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  @media screen and (max-width: 340px) {
    padding: 0;
  }
`;

const Card = styled.div`
  display: flex;
  padding: 24px 0px;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);

  &:first-child {
    padding-top: 0;
  }
  &:last-child {
    padding-bottom: 0;
    border: none;
  }
`;

const CardContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1 0 0;
`;

const CardContent = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */
`;

const CardHelperText = styled.p`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const PointIconWrapper = styled.div<{ bgColor: string }>`
  display: flex;
  width: 48px;
  min-width: 48px;
  height: 48px;
  padding: var(--radius-medium, 8px);
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: var(--radius-medium, 8px);
  background: ${(prop) => prop.bgColor};
`;

const PointIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const Button = styled.button`
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-green, #34c77e);
`;

const ButtonTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ButtonHighlightText = styled.p`
  color: var(--Light-Scheme-Monochrome-white, #fff);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const ButtonText = styled.p`
  color: var(--Light-Scheme-Monochrome-white, #fff);
  text-align: center;

  /* Display/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */
`;
